/**
 * Which shell the miniapp is running inside, as reported by the host over the SDK.
 *
 * This is NOT the same question as "is the viewport narrow". A 400px-wide desktop browser is `web`; a
 * tablet in the native app is `mobile`. Chrome decisions have to key off this — deciding by width is what
 * puts an in-app back button underneath the native one.
 */
export const HOST_PLATFORMS = {
  MOBILE: 'mobile',
  WEB: 'web',
  TENANT_PORTAL: 'tenant-portal',
} as const;

export type HostPlatform = typeof HOST_PLATFORMS[keyof typeof HOST_PLATFORMS];

/** `HQO_SDK_ACTIONS.SET_HEADER` — the wire name the host registers its handler under. */
const SET_HEADER_ACTION = 'setHeader';

/**
 * Whether a native shell is listening and can be told to render a header.
 *
 * `getHostInfo` cannot be relied on to answer this. The SDK posts to iOS via
 * `window.webkit.messageHandlers[action]` and to Android via `Android[action]` — routed per action name,
 * and dropped in silence when the app has registered no handler by that name. A host that never
 * implemented `getHostInfo` therefore never answers and never errors, so the platform stays unknown
 * forever and every chrome decision quietly resolves to desktop.
 *
 * The bridge has no such problem: it is present or it is not, synchronously, on the first render. It is
 * also the more honest question — not "what platform is this" but "is there a native header to defer to".
 *
 * Deliberately probing `setHeader` rather than the bridge in general, so the two halves of the decision
 * cannot disagree: this returns true only where the native back button can actually be installed. A shell
 * that cannot render one leaves the in-app affordance alone rather than stranding the page with no way out.
 */
export const isNativeHost = (): boolean => {
  const { webkit, Android } = window as Window & {
    webkit?: { messageHandlers?: Record<string, unknown> };
    Android?: Record<string, unknown>;
  };

  return Boolean(webkit?.messageHandlers?.[SET_HEADER_ACTION]) || Boolean(Android?.[SET_HEADER_ACTION]);
};

/** The hosts that reliably report themselves. Everything else has to be inferred. */
const WEB_HOSTS: ReadonlySet<string> = new Set([HOST_PLATFORMS.WEB, HOST_PLATFORMS.TENANT_PORTAL]);

/**
 * Whether the app is drawing its own chrome.
 *
 * Detects the web hosts positively and treats everything else as native, following the rule
 * miniapp-content settled on in VIS-1542: `platform` is only reliably populated for `web` and
 * `tenant-portal`, and native usually reports nothing at all. Detecting mobile positively means testing
 * for the one value that never arrives, which is why an in-app back button kept rendering under the
 * native header.
 *
 * Where this goes further than miniapp-content: an unreported platform is disambiguated by the bridge, so
 * a plain browser tab — no host, nothing to report — keeps its own chrome instead of being lumped in with
 * native and left with no way back at all.
 */
export const isDesktopHost = (platform?: HostPlatform): boolean =>
  platform ? WEB_HOSTS.has(platform) : !isNativeHost();
